<template>
  <div v-loading="loading">
    <eden-page-header
      :title="'Beauticians Directory'"
      :subtitle="formatFullName(beautician)"
    >
      <template slot="actions">
        <beautician-actions
          v-if="allowAccessFor(['admin', 'operations'])"
          :beautician="beautician"
          @update="update"
        >
          <template slot="actions-trigger">
            <el-button type="plain">
              Actions <i class="eden-icon-arrow-down"></i>
            </el-button>
          </template>
        </beautician-actions>
      </template>
    </eden-page-header>
    <eden-information-card>
      <template slot="content">
        <div class="beautician-profile">
          <div class="beautician-profile--summary">
            <eden-image :image-url="beautician.avatar" :image-size="70" />
            <p class="name">{{ formatFullName(beautician) }}</p>
            <span class="address">{{ beautician.address }}</span>
            <el-tag :class="'small'" :type="setType(beautician.status)">{{
              formatText(beautician.status)
            }}</el-tag>
            <el-button :type="'plain'" :size="'small'" @click="edit"
              >Edit Profile</el-button
            >
          </div>
          <eden-information-card
            class="no-border"
            :title="'General Information'"
          >
            <template slot="content">
              <el-table
                :class="'general split-cell'"
                :data="general"
                :show-header="false"
                :span-method="arraySpanMethod"
              >
                <el-table-column>
                  <template slot-scope="scope">
                    <template v-if="scope.$index < 2">
                      <el-table
                        :class="'inner-table'"
                        :data="scope.row.data"
                        :show-header="false"
                      >
                        <el-table-column>
                          <template v-slot="scope">
                            <span class="title font-xsm">{{
                              scope.row.label
                            }}</span>
                            <p class="text font-sm">
                              {{ scope.row.text }}
                            </p>
                          </template>
                        </el-table-column>
                      </el-table>
                    </template>
                    <template v-else>
                      <span class="title font-xsm">{{ scope.row.label }}</span>
                      <p class="text font-sm">
                        {{ scope.row.text }}
                      </p>
                    </template>
                  </template>
                </el-table-column>
              </el-table>
            </template>
          </eden-information-card>
          <eden-information-card
            class="no-border min-width"
            :title="'National Id'"
          >
            <template slot="content">
              <el-table
                :class="'general'"
                :data="nationalId"
                :show-header="false"
                :span-method="arraySpanMethod"
              >
                <el-table-column>
                  <template slot-scope="scope">
                    <span class="title font-xsm">{{ scope.row.label }}</span>
                    <p v-if="scope.row.text" class="text font-sm">
                      {{ scope.row.text }}
                    </p>
                    <img
                      v-if="scope.row.image"
                      :src="getImage(scope.row.image)"
                      :alt="'NIN'"
                    />
                  </template>
                </el-table-column>
              </el-table>
            </template>
          </eden-information-card>
        </div>
      </template>
    </eden-information-card>
    <beautician-orders />
  </div>
</template>

<script>
import BeauticianOrders from "@/components/Logistics/Directory/Beauticians/Beautician/BeauticianOrders.vue";
import BeauticianActions from "@/components/Logistics/Directory/Beauticians/Beautician/Actions/BeauticianActions";

import beauticians from "@/requests/logistics/directory/beauticians";

export default {
  name: "Beautician",
  components: { BeauticianActions, BeauticianOrders },

  data() {
    return {
      loading: false,
      beautician: {},
    };
  },

  computed: {
    beauticianId() {
      return this.$route.params.id;
    },
    general() {
      return [
        {
          data: [
            {
              label: "Phone Number",
              text: this.formatPhoneNumber(this.beautician.phone_number) || "-",
            },
            {
              label: "Alternate Phone Number",
              text:
                this.formatPhoneNumber(this.beautician.alternative_phone) ||
                "-",
            },
          ],
        },
        {
          data: [
            {
              label: "Location area they live in",
              text:
                this.beautician.location_area &&
                this.beautician.location_area.name,
            },
            {
              label: "Category",
              text:
                this.beautician.categories &&
                this.formatArrayToString({
                  data: this.beautician.categories,
                  property: "name",
                  separator: "\n",
                }),
            },
          ],
        },
        {
          label: "Preferred working days",
          text:
            this.beautician.preferred_working_days &&
            this.formatToTitleCase(
              this.sortWeekDays(
                this.beautician.preferred_working_days,
                "short"
              ).join(", ")
            ),
        },
      ];
    },
    nationalId() {
      return [
        {
          label: "National Identity Number",
          text: this.beautician.identification_number,
        },
        {
          label: "Attachment",
          image: this.beautician.identification_image,
        },
      ];
    },
  },
  created() {
    this.getBeautician();
  },
  methods: {
    getBeautician() {
      this.loading = true;
      beauticians
        .get(this.beauticianId)
        .then((response) => {
          const { status, data } = response.data;
          if (status) {
            this.beautician = data[0];
          } else {
            this.$message.error(response.data.message);
          }
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          const errorMessage = error.response.data;
          if (errorMessage.errors) {
            const errorKeys = Object.keys(errorMessage.errors);
            this.$message.error(errorMessage.errors[errorKeys[0]].join(""));
          } else {
            this.$message.error(errorMessage.message);
          }
        });
    },
    edit() {
      this.$router.push({
        name: "logistics.directory.beautician-edit",
        params: { id: this.beauticianId },
      });
    },
    update(action) {
      if (action === "delete") {
        this.$router.push({
          name: "logistics.directory.index",
          query: { t: "beauticians" },
        });
      } else {
        this.beautician.status =
          this.beautician.status === "active" ? "inactive" : "active";
      }
    },
    arraySpanMethod({ rowIndex }) {
      if (rowIndex < 2) {
        return [1, 2];
      } else if (rowIndex === 3) {
        return [3, 4];
      } else {
        return [1, 4];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$profile-width: 320px;

.information-card {
  padding: 0;
  margin-bottom: 48px;
  min-width: unset;

  &.no-border {
    border: 0;
    margin-bottom: 0;
    padding: 16px 0;
    width: 322px;

    &.min-width {
      width: calc(100% - 642px);
    }

    div.title {
      padding: 16px !important;
    }

    @media (min-width: 768px) {
      &:not(:last-child) {
        border-right: 1px solid #f0f4f2;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    @media (max-width: 1200px) {
      border-top: 1px solid #f0f4f2;
      border-radius: 0;
      width: 100%;
    }
  }
}

.beautician-profile {
  display: flex;

  &--summary {
    padding: 16px 0;
    max-width: $profile-width;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .name {
      margin: 7px 0;
      font-weight: 500;
      font-size: 1.125rem;
    }

    .address {
      font-size: 0.875rem;
      font-weight: 400;
      color: var(--eden-grey-tertiary);
      display: block;
      margin-bottom: 5px;
    }

    .el-button {
      margin: 15px auto 5px;
    }
  }

  @media (min-width: 1300px) {
    .beautician-profile--summary {
      border-right: 1px solid #f0f4f2;
    }
  }

  @media (max-width: 1300px) {
    flex-wrap: wrap;
  }
}

.general {
  tr:last-child {
    td {
      padding: 0;

      .cell {
        padding: 0;
      }
    }
  }

  img {
    display: block;
    height: 80px;
    width: auto;
    margin-top: 10px;
  }
}
.inner-table {
  tbody {
    display: flex;

    tr {
      width: 50%;
      padding: 12px;
      &:nth-child(2) {
        border-left: 1px solid #ebeef5;
      }
    }
  }
}
</style>
